import { c as _c } from "react/compiler-runtime";
import Script from 'next/script';
import { AdvScripts, LiveRampScript } from '@sbt-web/adv';
import { getCategoryByFriendly } from '@sbt-web/category-utils';
import { extractSbtHeaders, searchItems } from '@sbt-web/network/server';
import { CategoryID, HTTPStatusCode, type AdItem, type CarInfo, type FavCounter, type Shop, type ShopGoogleReviews, type TrustProfileInfo, type UserProfileInfo, type UserAdInfo, type SearchResult, type InfoPromo } from '@sbt-web/network/types';
import { applyContextToFunc } from '@sbt-web/request-id-handler';
import { nullifyUndefinedProperties, readEnvironmentIdFromHeaders } from '@sbt-web/utils';
import { LiraRunAdvScriptWrapperForAdview } from '@/client/components/Adv/Lira/LiraRunAdvScriptWrapper/LiraRunAdvScriptWrapperForAdview';
import { AdDetailContainer } from '@/client/components/Detail/Containers/AdDetailContainer';
import type { AdDetailContext } from '@/client/components/Detail/Containers/common-props';
import { SkeletonWithAdvForAdView } from '@/client/components/Detail/Containers/SkeletonWithAdv';
import { useAdDetailCommons } from '@/client/components/Detail/Containers/useAdDetailCommons';
import { AdDetailHeadTags } from '@/client/components/SEO/DetailHeadTags';
import { getTitle } from '@/client/components/SEO/DetailHeadTags/utils';
import type { AdDetailLinks } from '@/client/components/SEO/InternalLinks/types';
import Sitemap from '@/client/components/SEO/Sitemap';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import { markPerformance, Marks } from '@/client/utilities/performance/performanceMonitor';
import enrichAuto from '@/shared/helpers/CategorySpecificDetail/auto';
import { getHoustonBannerContent, type HoustonBannerContent } from '@/shared/helpers/getHoustonBannerContent';
import { unwrap } from '@/shared/helpers/Promise';
import { getFirstApplicablePromo } from '@/shared/helpers/PromoHelper';
import { getCategoryThemeById } from '@/shared/helpers/Themes';
import { getAdvertiserName } from '@/shared/models/Advertiser';
import { getErrorMessage } from '@/tools/errorHelpers';
import { adTypesToSlugMap } from '@/tools/search/values';
import { fetchItem } from '../server/detail-requests/item';
import getPlexusInternalLinks, { extractRequestId, isBotifyUserAgent } from '../server/detail-requests/plexus';
import { getShippingCosts, ShippingCosts } from '../server/detail-requests/ShippingCost';
import { getActivePromo, getAdvertiserProfile, getFavoriteCounter, getShopData, getShopGoogleReviewsData, getTrustInfo, getPrivateUserInfo, getUserAds } from '../server/detail-requests/subito-services';
import { BasePageProps, GonePageProps, setStatusOnServer } from '../types/basePageProps';
markPerformance(Marks.GLOBAL_RUN);
export type AdDetailPageProps = BasePageProps & {
  categorySpecificData: CarInfo | null;
  pageTitle: string;
  internalLinks: AdDetailLinks | null;
  shop: Shop | null;
  shopGoogleReviews: ShopGoogleReviews | null;
  favoriteCounter: FavCounter | null;
  advertiserProfile: UserProfileInfo | null;
  trustInfo: TrustProfileInfo | null;
  promo: InfoPromo | null;
  ad: AdItem;
  shippingCosts: ShippingCosts | null;
  houstonBanner?: HoustonBannerContent | null;
  privateUserInfo?: UserAdInfo | null;
  userAds?: SearchResult | null;
};
export default function AdDetail(props) {
  const $ = _c(38);
  const {
    ad,
    pageTitle,
    internalLinks,
    categorySpecificData,
    shop,
    shopGoogleReviews,
    favoriteCounter,
    advertiserProfile,
    trustInfo,
    promo,
    shippingCosts,
    houstonBanner,
    privateUserInfo,
    userAds
  } = props;
  const t0 = props.trustInfo?.presence.enabled;
  let t1;
  if ($[0] !== ad || $[1] !== t0) {
    t1 = {
      ad,
      trustPingEnabled: t0
    };
    $[0] = ad;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useAdDetailCommons(t1);
  const noAdvAdvertiser = ad.advertiser.proPremiumDetailEnabled;
  const {
    noAdvToggle,
    optimizelyReady
  } = useAdvToggle();
  let t2;
  if ($[3] !== ad || $[4] !== pageTitle) {
    t2 = <AdDetailHeadTags pageTitle={pageTitle} ad={ad} />;
    $[3] = ad;
    $[4] = pageTitle;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LiveRampScript Script={Script} />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AdvScripts Script={Script} config={{
      includeOw: false,
      includeTam: false,
      includeAdSense: true,
      includePrebid: true,
      publicPath: "/static/script"
    }} />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== ad || $[9] !== categorySpecificData || $[10] !== noAdvAdvertiser || $[11] !== noAdvToggle || $[12] !== optimizelyReady) {
    t5 = optimizelyReady && noAdvToggle !== "B" && !noAdvAdvertiser && <LiraRunAdvScriptWrapperForAdview ad={ad} infocarData={categorySpecificData} />;
    $[8] = ad;
    $[9] = categorySpecificData;
    $[10] = noAdvAdvertiser;
    $[11] = noAdvToggle;
    $[12] = optimizelyReady;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== ad || $[15] !== advertiserProfile || $[16] !== categorySpecificData || $[17] !== favoriteCounter || $[18] !== houstonBanner || $[19] !== internalLinks || $[20] !== privateUserInfo || $[21] !== promo || $[22] !== shippingCosts || $[23] !== shop || $[24] !== shopGoogleReviews || $[25] !== trustInfo || $[26] !== userAds) {
    t6 = <AdDetailContainer ad={ad} internalLinks={internalLinks} infocarData={categorySpecificData} shop={shop} shopGoogleReviews={shopGoogleReviews} favoriteCounter={favoriteCounter} advertiserProfile={advertiserProfile} trustInfo={trustInfo} promo={promo} shippingCosts={shippingCosts} houstonBanner={houstonBanner} privateUserInfo={privateUserInfo} userAds={userAds} />;
    $[14] = ad;
    $[15] = advertiserProfile;
    $[16] = categorySpecificData;
    $[17] = favoriteCounter;
    $[18] = houstonBanner;
    $[19] = internalLinks;
    $[20] = privateUserInfo;
    $[21] = promo;
    $[22] = shippingCosts;
    $[23] = shop;
    $[24] = shopGoogleReviews;
    $[25] = trustInfo;
    $[26] = userAds;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== noAdvAdvertiser || $[29] !== noAdvToggle || $[30] !== t5 || $[31] !== t6) {
    t7 = <SkeletonWithAdvForAdView noAdvToggle={noAdvToggle} noAdvAdvertiser={noAdvAdvertiser}>{t3}{t4}{t5}{t6}</SkeletonWithAdvForAdView>;
    $[28] = noAdvAdvertiser;
    $[29] = noAdvToggle;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  let t9;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Sitemap />;
    t9 = <Script strategy="afterInteractive" id="appsflyer-script" src="/static/script/appsflyer.js" />;
    $[33] = t8;
    $[34] = t9;
  } else {
    t8 = $[33];
    t9 = $[34];
  }
  let t10;
  if ($[35] !== t2 || $[36] !== t7) {
    t10 = <>{t2}{t7}{t8}{t9}</>;
    $[35] = t2;
    $[36] = t7;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  return t10;
}
const getProps = async (ctx: AdDetailContext): Promise<{
  props: AdDetailPageProps | GonePageProps;
} | {
  notFound: true;
} | {
  redirect: {
    destination: string;
    permanent: boolean;
  };
}> => {
  const {
    query,
    req,
    res
  } = ctx;
  const {
    id
  } = query;
  if (!id) {
    return {
      notFound: true
    };
  }
  let environmentId: string | undefined = undefined;
  try {
    if (req != null) {
      const eid = readEnvironmentIdFromHeaders(req);
      if (eid != null) {
        environmentId = eid;
      }
    }
  } catch (error) {
    console.error('Could not set the Environment ID on the serverside,', getErrorMessage(error));
  }
  const item = await fetchItem(id, environmentId, req);
  const category = getCategoryByFriendly(query.category);
  if (item == null) {
    const defaultAdType = category.defaultAdType;
    const categoryDefaultUrl = `/annunci-italia/${adTypesToSlugMap[defaultAdType]}/${category.friendly}/`;
    let gonePageSuggestions: AdItem[];
    try {
      const searchResponse = await searchItems(process.env.ANUBI_BASE_URL as string, {
        lim: '12',
        t: defaultAdType,
        ...(category.id !== CategoryID.Tutte ? {
          c: category.id
        } : {})
      }, extractSbtHeaders(req), true);
      gonePageSuggestions = searchResponse.body.ads;
    } catch {
      gonePageSuggestions = [];
    }
    return {
      props: {
        ...setStatusOnServer(HTTPStatusCode.Gone, res),
        categoryLabel: category.label,
        categoryDefaultUrl,
        categoryId: category.id,
        items: gonePageSuggestions
      } satisfies GonePageProps
    };
  }
  const houstonBannerPromise = getHoustonBannerContent();

  // Augment the response:
  const [shopData, shopGoogleReviewsData, categorySpecificData, internalLinksResponse, favoriteCounter, advertiserProfileResponse, trustInfoResponse, promosResponse, shippingCostResponse, houstonBannerResponse, privateUserInfoResponse, userAdsResponse] = await Promise.allSettled([getShopData(item.advertiser, res, req), getShopGoogleReviewsData(item.advertiser, res, req), item.category.id === CategoryID.Auto ? enrichAuto(item, req) : null, getPlexusInternalLinks({
    pageUrl: item.urls.default,
    category: item.category,
    geo: item.geo,
    baseQuery: item.subject
  }, res, isBotifyUserAgent(req), extractRequestId(req)), getFavoriteCounter(item.urn, res, req), getAdvertiserProfile(item.advertiser.userId, res, req), getTrustInfo(item.advertiser, res, req), getActivePromo(res, req), getShippingCosts(item, req), houstonBannerPromise, getPrivateUserInfo(item.advertiser.userId, item.advertiser, res, req), getUserAds(item.advertiser.userId, item.advertiser, res, req)]);
  const promos = unwrap(promosResponse);
  const promo = getFirstApplicablePromo(item, promos);
  const augmentResponse = {
    shop: unwrap(shopData),
    shopGoogleReviews: unwrap(shopGoogleReviewsData),
    categorySpecificData: unwrap(categorySpecificData),
    internalLinks: unwrap(internalLinksResponse),
    favoriteCounter: unwrap(favoriteCounter),
    advertiserProfile: unwrap(advertiserProfileResponse),
    trustInfo: unwrap(trustInfoResponse),
    shippingCosts: unwrap(shippingCostResponse),
    promo,
    houstonBanner: unwrap(houstonBannerResponse),
    privateUserInfo: unwrap(privateUserInfoResponse),
    userAds: unwrap(userAdsResponse)
  };
  return {
    props: nullifyUndefinedProperties({
      ...augmentResponse,
      statusCode: HTTPStatusCode.OK,
      category: item.category,
      ad: item,
      theme: getCategoryThemeById(item.category.id),
      pageName: 'addetail',
      pageTitle: getTitle(item, getAdvertiserName(item.advertiser.type, augmentResponse.shop, augmentResponse.advertiserProfile))
    })
  };
};
const wrappedGetProps = applyContextToFunc(getProps);
export { wrappedGetProps as getServerSideProps };